import { FormEvent, useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { apiRequest, getAuthToken, getAuthUser, setAuthToken, setAuthUser, API_BASE } from '../lib/api';
import { showToast } from '../components/Toast';

type AuthResponse = {
  token: string;
  user: {
    id: string;
    email: string;
    role: 'ADMIN' | 'USER';
  };
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const authenticatedUser = getAuthUser();
  const hasValidSession = Boolean(getAuthToken() && authenticatedUser);
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [cover, setCover] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0); // số giây phải đợi trước khi gửi lại

  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  const passwordMessage = 'Mật khẩu phải từ 8 ký tự, gồm chữ hoa, chữ thường, số và ký tự đặc biệt (@$!%*?&)';

  useEffect(() => {
    async function loadMeta() {
      try {
        const res = await fetch(`${API_BASE}/posts`);
        const data = await res.json();
        const metaPost = data.posts?.find((p: any) => p.slug === 'site-meta');
        if (metaPost) {
          const metaData = JSON.parse(metaPost.content);
          if (metaData.cover) setCover(metaData.cover);
        }
      } catch (e) {
        console.error('Failed to load site meta', e);
      }
    }
    loadMeta();
  }, []);

  useEffect(() => {
    if (!location.state?.sessionExpired) return;
    const timer = window.setTimeout(() => {
      showToast('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.', 'error');
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.state]);

  // Countdown OTP cooldown
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [otpCooldown]);

  if (hasValidSession && !location.state?.sessionExpired) {
    return <Navigate to={authenticatedUser?.role === 'ADMIN' ? '/admin' : '/account'} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (
      (mode === 'register' && !otpStep) ||
      (mode === 'forgot' && otpStep)
    ) {
      if (!passwordRegex.test(password)) {
        showToast(passwordMessage, 'error');
        return;
      }
    }

    setBusy(true);

    try {
      if (mode === 'login') {
        const payload = { email, password };
        const response = await apiRequest<AuthResponse>('/auth/login', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        
        setAuthToken(response.token);
        setAuthUser(response.user);
        
        showToast('Đăng nhập thành công. Đang chuyển hướng...', 'success');
        if (response.user.role === 'ADMIN') {
          navigate('/admin');
        } else {
          navigate('/account');
        }
      } else if (mode === 'register' && !otpStep) {
        // Gửi OTP
        const res = await apiRequest<any>('/auth/send-otp', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        setOtpStep(true);
        setOtpCooldown(60);
        showToast('Đã gửi mã OTP đến email của bạn. Mã hết hạn sau 5 phút.', 'info');
      } else if (mode === 'register' && otpStep) {
        // Đăng ký với OTP
        const payload = { email, password, phone: phone || undefined, otp };
        const response = await apiRequest<AuthResponse>('/auth/register', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        
        setAuthToken(response.token);
        setAuthUser(response.user);
        
        showToast('Đăng ký thành công. Đang chuyển hướng...', 'success');
        navigate('/account');
      } else if (mode === 'forgot' && !otpStep) {
        // Gửi OTP quên mật khẩu
        await apiRequest('/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        setOtpStep(true);
        setOtpCooldown(60);
        showToast('Đã gửi mã OTP để đặt lại mật khẩu. Mã hết hạn sau 5 phút.', 'info');
      } else if (mode === 'forgot' && otpStep) {
        // Khôi phục mật khẩu
        await apiRequest('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({ email, otp, password }),
        });
        
        showToast('Đặt lại mật khẩu thành công! Vui lòng đăng nhập.', 'success');
        setMode('login');
        setOtpStep(false);
        setPassword('');
      }
    } catch (e: any) {
      // Nếu backend trả về 429 kèm waitSeconds thì khởi động đếm ngược ngay
      if (e?.waitSeconds) setOtpCooldown(e.waitSeconds);
      showToast(e instanceof Error ? e.message : 'Đã có lỗi xảy ra', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      {/* Left panel - branding */}
      <div className="login-brand-panel" style={cover ? { backgroundImage: `url(${cover})` } : {}}>
        <div className="login-brand-content">
          <div className="login-logo-mark">3D</div>
          <h2>3DSmart H&amp;L Store</h2>
          <p>Giải pháp công nghệ in 3D và IoT toàn diện — Sáng tạo · Chất lượng · Tận tâm</p>
          <div className="login-features">
            <div className="login-feature-item">
              <span className="login-feature-icon">🖨️</span>
              <span>In 3D chất lượng cao</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-icon">📡</span>
              <span>Giải pháp IoT thông minh</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-icon">🛡️</span>
              <span>Uy tín - Chất lượng đảm bảo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="login-form-panel">
        <div className="login-form-inner">
          <div style={{ marginBottom: '32px' }}>
            <span className="eyebrow">Tài khoản</span>
            <h1 className="login-title">
              {mode === 'login' ? 'Đăng nhập hệ thống' : mode === 'register' ? 'Tạo tài khoản mới' : 'Khôi phục mật khẩu'}
            </h1>
            <p className="login-subtitle">
              {mode === 'login'
                ? 'Chào mừng bạn quay lại! Đăng nhập để quản lý thiết bị BMCU.'
                : mode === 'register' 
                  ? (otpStep ? 'Vui lòng kiểm tra email và nhập mã xác nhận 6 số để hoàn tất.' : 'Tạo tài khoản để kích hoạt bảo hành và theo dõi firmware.')
                  : (otpStep ? 'Vui lòng kiểm tra email và nhập mã xác nhận 6 số cùng mật khẩu mới.' : 'Nhập email của bạn để nhận mã khôi phục mật khẩu.')}
            </p>
          </div>

          <form onSubmit={onSubmit} className="login-form">
            {/* Email - Always show on step 1 or login */}
            {(!otpStep || mode === 'login') && (
              <div className="login-field">
                <label className="field-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="input-box login-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
            )}

            {/* Password - Hide on forgot step 1, show otherwise */}
            {((!otpStep && mode !== 'forgot') || (otpStep && mode === 'forgot') || mode === 'login') && (
              <div className="login-field">
                <label className="field-label" htmlFor="password">
                  {mode === 'forgot' ? 'Mật khẩu mới' : 'Mật khẩu'}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="input-box login-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    required
                    minLength={8}
                    style={{ paddingRight: '40px', width: '100%', boxSizing: 'border-box' }}
                  />
                  {password.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute', right: '0', top: '0', bottom: '0', width: '44px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, margin: 0,
                        background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'
                      }}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg style={{ transform: 'translateY(-4px)' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="22" height="22">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      ) : (
                        <svg style={{ transform: 'translateY(-4px)' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="22" height="22">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {mode === 'register' && !otpStep && (
              <div className="login-field">
                <label className="field-label" htmlFor="phone">Số điện thoại (tùy chọn)</label>
                <input
                  id="phone"
                  type="text"
                  className="input-box login-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0900 000 000"
                />
              </div>
            )}

            {(mode === 'register' || mode === 'forgot') && otpStep && (
              <div className="login-field">
                <label className="field-label" htmlFor="otp">Mã xác nhận (OTP)</label>
                <input
                  id="otp"
                  type="text"
                  className="input-box login-input"
                  style={{ fontSize: '20px', letterSpacing: '4px', textAlign: 'center', fontWeight: 'bold' }}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="------"
                  maxLength={6}
                  required
                />
              </div>
            )}

            <button type="submit" className="button primary login-submit-btn" disabled={busy} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
              {busy ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  <span>Đang xử lý...</span>
                </>
              ) : (
                <span>{mode === 'login' ? 'Đăng nhập' : otpStep ? 'Xác nhận OTP' : 'Tiếp tục'}</span>
              )}
            </button>

            {/* Nút gửi lại OTP với countdown - chỉ hiển thị ở bước 2 */}
            {otpStep && (mode === 'register' || mode === 'forgot') && (
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                {otpCooldown > 0 ? (
                  <span style={{ color: '#64748b', fontSize: '14px' }}>
                    Gửi lại mã sau <strong style={{ color: '#f43f5e' }}>{otpCooldown}s</strong>
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const endpoint = mode === 'register' ? '/auth/send-otp' : '/auth/forgot-password';
                        await apiRequest(endpoint, {
                          method: 'POST',
                          body: JSON.stringify({ email }),
                        });
                        setOtpCooldown(60);
                        showToast('Đã gửi lại mã OTP mới. Mã hết hạn sau 5 phút.', 'info');
                      } catch (e: any) {
                        if (e?.waitSeconds) setOtpCooldown(e.waitSeconds);
                        showToast(e instanceof Error ? e.message : 'Không thể gửi lại mã.', 'error');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    style={{ background: 'none', border: 'none', color: '#f43f5e', fontSize: '14px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Gửi lại mã OTP
                  </button>
                )}
              </div>
            )}

            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>

            {mode === 'login' && (
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button type="button" onClick={() => { setMode('forgot'); }} style={{
                  background: 'none', border: 'none', color: '#f43f5e', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                }}>
                  Quên mật khẩu?
                </button>
              </div>
            )}

            <div className="login-divider">
              <span>hoặc</span>
            </div>

            <button
              type="button"
              className="login-switch-btn"
              onClick={() => { 
                if (otpStep) {
                  setOtpStep(false);
                } else {
                  setMode(mode === 'login' ? 'register' : 'login'); 
                  setOtpStep(false);
                }
              }}
              disabled={busy}
            >
              {mode === 'login' 
                ? 'Chưa có tài khoản? Đăng ký ngay →' 
                : (otpStep ? '← Quay lại nhập thông tin' : 'Đã có tài khoản? Đăng nhập →')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
